import type { Item } from '../types/tracking.types';

/**
 * Detecta se está em ambiente de desenvolvimento
 */
export const isDevelopment = (): boolean => {
  return import.meta.env.DEV || import.meta.env.VITE_DEVELOPMENT_MODE === 'true' || window.location.hostname === 'localhost';
};

const ATTRIBUTION_TTL_DAYS = 28;
/** UTM first/last: janela maior (alinhada ao checkoutTD.js da loja). */
const UTM_TTL_DAYS = 90;

function getCookie(name: string): string | null {
  try {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  } catch {
    return null;
  }
}

function setAttributionCookie(name: string, value: string, ttlDays: number = ATTRIBUTION_TTL_DAYS): void {
  try {
    const maxAge = ttlDays * 24 * 60 * 60;
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
  } catch {
    // ignore
  }
}

/** Captura ttclid (TikTok) da URL ou cookie e persiste em cookie */
export const getTtclid = (): string | undefined => {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('ttclid');
    if (fromUrl) {
      setAttributionCookie('ttclid', fromUrl);
      return fromUrl;
    }
    return getCookie('ttclid') ?? undefined;
  } catch {
    return undefined;
  }
};

/** Captura gclid (Google Ads) da URL ou cookie e persiste em cookie */
export const getGclid = (): string | undefined => {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('gclid');
    if (fromUrl) {
      setAttributionCookie('gclid', fromUrl);
      return fromUrl;
    }
    return getCookie('gclid') ?? undefined;
  } catch {
    return undefined;
  }
};

// ===== Atribuição UTM first-touch + last-touch =====

export interface UtmTouch {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  src?: string;
  sck?: string;
  at?: string; // ISO 8601 — quando o toque foi capturado
}

export interface UtmTracking {
  first?: UtmTouch;
  last?: UtmTouch;
}

/** Lê UTM/src/sck da URL atual. Retorna null se a URL não tiver nenhum. */
function readUtmsFromUrl(): UtmTouch | null {
  try {
    const p = new URLSearchParams(window.location.search);
    const touch: UtmTouch = {
      utm_source: p.get('utm_source') ?? undefined,
      utm_medium: p.get('utm_medium') ?? undefined,
      utm_campaign: p.get('utm_campaign') ?? undefined,
      utm_content: p.get('utm_content') ?? undefined,
      utm_term: p.get('utm_term') ?? undefined,
      src: p.get('src') ?? undefined,
      sck: p.get('sck') ?? undefined,
    };
    const has = Object.values(touch).some((v) => v != null && v !== '');
    if (!has) return null;
    touch.at = new Date().toISOString();
    return touch;
  } catch {
    return null;
  }
}

function parseTouchCookie(name: string): UtmTouch | undefined {
  const raw = getCookie(name);
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as UtmTouch;
  } catch {
    return undefined;
  }
}

function parseTouchQueryParam(name: string): UtmTouch | undefined {
  try {
    const raw = new URLSearchParams(window.location.search).get(name);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as UtmTouch;
    if (!parsed || typeof parsed !== 'object') return undefined;
    const has = Object.values(parsed).some((v) => v != null && v !== '');
    return has ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function readOfferTrackingSeed(): UtmTracking | undefined {
  try {
    const raw = sessionStorage.getItem('shopifyOffer');
    if (!raw) return undefined;
    const offer = JSON.parse(raw);
    const t = offer?.tracking;
    if (!t || typeof t !== 'object') return undefined;
    if (!t.first && !t.last) return undefined;
    return t as UtmTracking;
  } catch {
    return undefined;
  }
}

/**
 * Captura UTM/src/sck e mantém atribuição first-touch + last-touch em cookie (TTL acima).
 * Fontes (híbrido): query flat, `td_first`/`td_last`, cookie do checkout, seed da oferta.
 * - `first`: gravado uma única vez (aquisição).
 * - `last`: atualizado quando a URL traz UTM nova.
 */
export const captureUtmTracking = (seed?: UtmTracking | null): UtmTracking => {
  const fromUrl = readUtmsFromUrl();
  const fromTdFirst = parseTouchQueryParam('td_first');
  const fromTdLast = parseTouchQueryParam('td_last');
  const offerSeed = seed ?? readOfferTrackingSeed();

  let first = parseTouchCookie('td_utm_first');
  if (!first && fromTdFirst) {
    first = fromTdFirst;
    setAttributionCookie('td_utm_first', JSON.stringify(fromTdFirst), UTM_TTL_DAYS);
  }
  if (!first && offerSeed?.first) {
    first = offerSeed.first;
    setAttributionCookie('td_utm_first', JSON.stringify(offerSeed.first), UTM_TTL_DAYS);
  }
  if (!first && fromUrl) {
    first = fromUrl;
    setAttributionCookie('td_utm_first', JSON.stringify(fromUrl), UTM_TTL_DAYS);
  }

  let last: UtmTouch | undefined;
  if (fromUrl) {
    last = fromUrl;
    setAttributionCookie('td_utm_last', JSON.stringify(fromUrl), UTM_TTL_DAYS);
  } else if (fromTdLast) {
    last = fromTdLast;
    setAttributionCookie('td_utm_last', JSON.stringify(fromTdLast), UTM_TTL_DAYS);
  } else {
    last = parseTouchCookie('td_utm_last') ?? offerSeed?.last ?? offerSeed?.first;
  }

  if (first && !last) last = first;
  if (last && !first) {
    first = last;
    setAttributionCookie('td_utm_first', JSON.stringify(last), UTM_TTL_DAYS);
  }

  const tracking: UtmTracking = {};
  if (first) tracking.first = first;
  if (last) tracking.last = last;
  return tracking;
};

/**
 * Extrai o Facebook Browser Parameter (fbp) dos cookies
 */
export const getFacebookBrowserParameter = (): string | undefined => {
  try {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === '_fbp') {
        return decodeURIComponent(value);
      }
    }
    return undefined;
  } catch (error) {
    return undefined;
  }
};

/**
 * Calcula o número total de itens no carrinho
 */
export const calculateNumItems = (items: Item[]): number => {
  return items.reduce((total, item) => total + (item.quantity || 1), 0);
};

/**
 * Gera um ID único universal para o evento baseado na oferta (usado em todas as plataformas)
 */
export const generateUniversalEventId = (offerNanoId: string, eventName: string): string => {
  const timestamp = Date.now().toString(36);
  const eventPrefix = eventName.substring(0, 3); // Primeiras 3 letras do evento
  return `${offerNanoId}_${eventPrefix}_${timestamp}`;
};

/**
 * Valida se os dados mínimos estão presentes
 */
export const validateEventData = (eventName: string, event: any): boolean => {
  if (!event.ecommerce) {
    return false;
  }
  
  if (!event.ecommerce.currency || !event.ecommerce.value) {
    return false;
  }
  
  return true;
}; 